import { Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useDownloadSewExports } from '@/backoffice/features/data-exports/hooks/use-download-sew-exports/use-download-sew-exports';
import { getLatestExportableBillingCycle } from '@/backoffice/features/data-exports/utils/latest-exportable-billing-cycle';
import { FileDownloadIcon } from '@/shared/assets/icons/file-download-icon';
import { ButtonWrapper } from '@/shared/components/ui/button/button-wrapper/button-wrapper';
import { FeatureCard } from '@/shared/components/ui/feature-card/feature-card';
import { Form } from '@/shared/components/ui/form/form';
import { TSubmittedData } from '@/shared/components/ui/form/form.types';
import { ModalController } from '@/shared/components/ui/modal-controller/modal-controller';
import { ModalContainer } from '@/shared/components/ui/modal-controller/modal-controller.styled';
import { useDateUtils } from '@/shared/hooks/use-date-utils/use-date-utils';
import { isoDateToBillingTs } from '@/shared/utils/format';
import { showToastError } from '@/shared/utils/toast';

import { SewBillingCycleExportForm } from './sew-billing-cycle-export-form';
import {
  TSewBillingCycleExportInputSchema,
  TSewBillingCycleExportProps,
} from './sew-billing-cycle-export.types';
import { sewBillingCycleExportValidationInputSchema } from './sew-billing-cycle-export.validation';

export const SewBillingCycleExport: React.FC<TSewBillingCycleExportProps> = ({
  title,
  description,
  loaderText,
  baseFileNamePrefix,
  regionOptions,
  fetchExport,
}) => {
  const { t } = useTranslation();
  const { formatLocalTime } = useDateUtils();
  const downloadExports = useDownloadSewExports();

  const onSubmit = async (
    data: TSubmittedData<TSewBillingCycleExportInputSchema>,
    handleClose: () => void,
  ) => {
    const { billingCycleReferenceTs, region } = data.data;
    const formattedCycle = formatLocalTime(
      billingCycleReferenceTs,
      'UTC',
      'MMMM_yyyy',
    ).toLowerCase();
    const baseFileName = `${baseFileNamePrefix}_${formattedCycle}`;

    try {
      const {
        url: exportUrl,
        log: logFile,
        hasErrors,
      } = await fetchExport(
        isoDateToBillingTs(billingCycleReferenceTs),
        region,
      );
      downloadExports({ exportUrl, logFile, baseFileName, hasErrors });
      handleClose();
    } catch {
      showToastError(t('billing:error_generating_export'));
    }
  };

  return (
    <ModalController
      trigger={
        <ButtonWrapper>
          <FeatureCard
            icon={<FileDownloadIcon />}
            title={title}
            description={description}
          />
        </ButtonWrapper>
      }
    >
      {(handleClose) => (
        <ModalContainer gap="2rem" width="30rem">
          <Typography variant="h6">{title}</Typography>
          <Form<TSewBillingCycleExportInputSchema>
            onSubmit={(data) => onSubmit(data, handleClose)}
            schema={sewBillingCycleExportValidationInputSchema(!!regionOptions)}
            loaderText={loaderText}
            defaultValues={{
              billingCycleReferenceTs:
                getLatestExportableBillingCycle().toISOString(),
            }}
          >
            {() => (
              <SewBillingCycleExportForm
                handleClose={handleClose}
                regionOptions={regionOptions}
              />
            )}
          </Form>
        </ModalContainer>
      )}
    </ModalController>
  );
};
